// Response shapes come from the shared server/client contracts (SCRUM-983).
import type { AxiosResponse } from "axios";
import type {
  PendingPagesResponse,
  PendingPageAddResponse,
  PendingPageDeleteResponse,
  MessageResponse,
} from "../../../../shared/apiTypes";
import api from "../api";
import createAppAsyncThunk from "../createAppAsyncThunk";
import {
  ADD_PENDING_PAGE,
  GET_NON_PUBLISHED_PAGES,
  GET_PUBLISHED_PAGES,
  PUBLISH,
  DELETE,
  CLEAR_MESSAGE,
} from "./pendingPagesTypes";
import { getHistories } from "../historiesServices/historiesActions";
import { refetchPageList } from "../pageServices/pagesSlice";

export const clearMessage = () => (dispatch) => {
  dispatch({
    type: CLEAR_MESSAGE,
  });
};

export const addPendingPage = (pendingpage, user) => async (dispatch) => {
  const res: AxiosResponse<PendingPageAddResponse> = await api.post(
    "/api/pendingpage/addpendingpage",
    { pendingpage, user }
  );
  dispatch({
    type: ADD_PENDING_PAGE,
    payload: res.data,
  });
  dispatch(getHistories());
  // Success is confirmed by the caller: it awaits this promise and toasts in its
  // own screen context (PagesContainer, or the editor save handlers), so there is
  // no global success sentinel to translate. A failed POST rejects and propagates,
  // so the caller won't confirm/redirect on failure (SCRUM-815/883).
  return res.data;
};

// SCRUM-1019: createAppAsyncThunk + guarded reverse. The old thunks had no catch
// (unhandled rejection on failure) and crashed on a non-array response. Legacy
// GET_* actions kept (pendingPagesSlice answers them by type, SCRUM-1951).
export const getNonPublishedPages = createAppAsyncThunk(
  "pendingPages/getNonPublished",
  async (_arg: void, { dispatch }) => {
    const res: AxiosResponse<PendingPagesResponse> = await api.get("/api/pendingpage/get-non-published-pages");
    dispatch({
      type: GET_NON_PUBLISHED_PAGES,
      payload: Array.isArray(res.data) ? [...res.data].reverse() : [],
    });
    return res.data;
  }
);

export const getPublishedPages = createAppAsyncThunk(
  "pendingPages/getPublished",
  async (_arg: void, { dispatch }) => {
    const res: AxiosResponse<PendingPagesResponse> = await api.get("/api/pendingpage/get-published-pages");
    dispatch({
      type: GET_PUBLISHED_PAGES,
      payload: Array.isArray(res.data) ? [...res.data].reverse() : [],
    });
    return res.data;
  }
);
// SCRUM-840: createAppAsyncThunk. The two positional args become one object
// so it fits the single-arg thunk API; the caller awaits .unwrap() so a failed
// publish surfaces via the hub instead of an unhandled rejection.
export const PublishPage = createAppAsyncThunk(
  "pendingPages/publish",
  async ({ pageId, ppageId }: { pageId: string; ppageId: string }, { dispatch }) => {
    const res: AxiosResponse<MessageResponse> = await api.put(`/api/pendingpage/publish/${pageId}/${ppageId}`);
    dispatch({ type: PUBLISH, payload: { msg: res.data.msg, id: ppageId } });
    // SCRUM-1811: the Custom Pages table holds one server page; fetch it again.
    dispatch(refetchPageList());
    dispatch(getHistories());
    return res.data;
  }
);

// SCRUM-840: createAppAsyncThunk so a failed DELETE rejects (the caller no
// longer shows "Entry removed" on failure) and surfaces via the central hub.
export const DeletePendingPage = createAppAsyncThunk(
  "pendingPages/delete",
  async (id: string, { dispatch }) => {
    const res: AxiosResponse<PendingPageDeleteResponse> = await api.delete(`/api/pendingpage/delete/${id}`);
    dispatch({ type: DELETE, payload: res.data.deletedPage });
    dispatch(getHistories());
    return res.data.deletedPage;
  }
);

